import { useState, useEffect, useCallback } from 'react';
import { Layout, notification } from 'antd';
import AppHeader from './appHeader/AppHeader';
import SideMenu from './sideMenu/SideMenu';
import PageContent from './pageContent/PageContent';
import ActionFloatButtons from './pageContent/ActionFloatButtons/ActionFloatButtons';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

interface AppLayoutProps {
    clearAuthDataFromState: () => Promise<void>;
}

const AppLayout: React.FC<AppLayoutProps> = ({ clearAuthDataFromState }) => {
    const auth = useSelector((state: RootState) => state.auth.auth);
    const authData = auth?.data && auth.data.length > 0 ? auth.data[0] : null;
    const { globalUser } = useSelector((state: RootState) => state.users);
    const { globalLocation } = useSelector((state: RootState) => state.locations);

    const [showSideMenu, setShowSideMenu] = useState(true);
    const [isMobile, setIsMobile] = useState(false);

    const { t, i18n } = useTranslation();

    useEffect(() => {
        dayjs.locale(i18n.language);
    }, [i18n.language]);

    const checkMobile = useCallback(() => {
        const mobile = window.innerWidth <= 768;
        setIsMobile(mobile);

        if (!mobile && !showSideMenu) {
            setShowSideMenu(true);
        }
    }, [showSideMenu]);

    useEffect(() => {
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, [checkMobile]);

    useEffect(() => {
        if (authData && (!globalUser || !globalLocation)) {
            console.warn('Missing global user or location — logging out for safety.');

            notification.error({
                message: t('notification.error'),
                description: t('auth.loginFailed'),
                placement: 'bottomRight',
            });
            clearAuthDataFromState();
        }
    }, [t, authData, globalUser, globalLocation, clearAuthDataFromState]);


    return (
        <Layout className="op-app">
            {(showSideMenu || !isMobile) && (
                <SideMenu
                    onClose={() => setShowSideMenu(false)}
                    isMobile={isMobile}
                />
            )}
            <Layout style={{
                transition: 'margin 0.3s'
            }}>
                <AppHeader
                    showSideMenu={showSideMenu}
                    onToggleMenu={() => setShowSideMenu(!showSideMenu)}
                    isMobile={isMobile}
                />
                <PageContent />
                <ActionFloatButtons />
            </Layout>
        </Layout>
    );
};

export default AppLayout;